import { doc, getDoc, getDocs, updateDoc, serverTimestamp, writeBatch,
  collection, query, where, orderBy, startAt, endAt, documentId
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { logAdminActionAtomic } from '@/lib/auditLogger';
import type { Game, User } from '@/lib/types';

async function requireAppAdmin(userId: string): Promise<void> {
  const snap = await getDoc(doc(db, 'users', userId));
  if (!snap.exists()) throw new Error('User not found');
  const user = snap.data() as User;
  if (user.roles?.app_admin !== true) throw new Error('Admin role required');
}

type MillisTimestamp = {
  toMillis: () => number;
};

function completionMillis(value: unknown): number | null {
  if (!value) return null;

  if (typeof value === 'object') {
    if (value instanceof Date) {
      const millis = value.getTime();
      return Number.isFinite(millis) ? millis : null;
    }

    const maybeTimestamp = value as Partial<MillisTimestamp>;
    if (typeof maybeTimestamp.toMillis === 'function') {
      const millis = maybeTimestamp.toMillis();
      return Number.isFinite(millis) ? millis : null;
    }
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const millis = new Date(value).getTime();
    return Number.isFinite(millis) ? millis : null;
  }

  return null;
}

export async function deleteGame(gameId: string, userId: string): Promise<void> {
  await requireAppAdmin(userId);

  const gameRef = doc(db, 'games', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const game = gameSnap.data() as Game;

  const batch = writeBatch(db);

  batch.update(gameRef, {
    status: 'cancelled',
    is_deleted: true,
    deleted_at: serverTimestamp(),
    deleted_by: userId,
  });

  logAdminActionAtomic(batch, 'delete_game', userId, { previous_status: game.status }, gameId, null);

  await batch.commit();
}

export async function hardDeleteGame(gameId: string, userId: string): Promise<void> {
  await requireAppAdmin(userId);

  const gameRef = doc(db, 'games', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error(`Game not found: ${gameId}`);

  const game = gameSnap.data() as Game;

  // 1. Query related records in parallel
  const [txSnap, attemptsSnap] = await Promise.all([
    getDocs(query(collection(db, 'settlement_transactions'), where('game_id', '==', gameId))),
    getDocs(query(collection(db, 'settlement_attempts'), where('game_id', '==', gameId))),
  ]);

  // 2. Query idempotency keys by document ID prefix
  const [idempotencySnap, idempotencyFinalizedSnap] = await Promise.all([
    getDocs(query(
      collection(db, 'idempotency_keys'),
      orderBy(documentId()),
      startAt(`settlement_${gameId}_`),
      endAt(`settlement_${gameId}_\uf8ff`)
    )),
    getDocs(query(
      collection(db, 'idempotency_keys'),
      orderBy(documentId()),
      startAt(`settlement_finalized_${gameId}`),
      endAt(`settlement_finalized_${gameId}\uf8ff`)
    )),
  ]);

  // 3. Check active game lock
  const lockRef = doc(db, 'locks', 'active_game');
  const lockSnap = await getDoc(lockRef);
  let clearLock = false;
  if (lockSnap.exists()) {
    const lockData = lockSnap.data() as { gameId?: string };
    if (lockData?.gameId === gameId) {
      clearLock = true;
    }
  }

  // 4. Build batch
  const batch = writeBatch(db);
  let opCount = 0;

  batch.delete(gameRef);
  opCount++;

  txSnap.docs.forEach(d => { batch.delete(d.ref); opCount++; });
  attemptsSnap.docs.forEach(d => { batch.delete(d.ref); opCount++; });
  idempotencySnap.docs.forEach(d => { batch.delete(d.ref); opCount++; });
  idempotencyFinalizedSnap.docs.forEach(d => { batch.delete(d.ref); opCount++; });

  if (clearLock) {
    batch.update(lockRef, { gameId: null, updatedAt: serverTimestamp() });
    opCount++;
  }

  // 5. Audit trail (atomic with deletion — audit log NOT deleted)
  logAdminActionAtomic(batch, 'hard_delete_game', userId, { previous_status: game.status }, gameId, null);
  opCount++;

  if (opCount > 500) {
    throw new Error(`Too many operations (${opCount}) for a single batch (max 500). Aborting.`);
  }

  // 6. Commit
  // Note: Between querying and committing, concurrent processes could create new
  // related docs. Those would be orphaned since the parent game is gone.
  // This is an inherent limitation of batch+query (Firestore transactions
  // don't support collection queries). For practical game sizes this is acceptable.
  await batch.commit();
}

export async function dismissGame(gameId: string): Promise<void> {
  await updateDoc(doc(db, 'games', gameId), {
    dismissed_from_active: true,
  });
}

export async function archiveStaleGames(games: Game[]): Promise<void> {
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const gamesToArchive = games.filter(g => {
    const completedAt = completionMillis(g.settlement?.milestones?.settlement_completed?.completed_at);
    return g.status === 'settled' && completedAt !== null && completedAt < thirtyDaysAgo;
  });

  if (gamesToArchive.length === 0) return;

  const batch = writeBatch(db);
  gamesToArchive.forEach(g => {
    batch.update(doc(db, 'games', g.id), {
      status: 'archived',
      archived_at: serverTimestamp(),
    });
  });
  await batch.commit();
}

export interface PurgeResult {
  gamesPurged: number;
  errors: string[];
}

export async function purgeSoftDeletedGames(userId: string): Promise<PurgeResult> {
  await requireAppAdmin(userId);

  const result: PurgeResult = { gamesPurged: 0, errors: [] };

  const gamesSnap = await getDocs(query(
    collection(db, 'games'),
    where('is_deleted', '==', true)
  ));

  if (gamesSnap.empty) return result;

  for (const gameDoc of gamesSnap.docs) {
    try {
      await hardDeleteGame(gameDoc.id, userId);
      result.gamesPurged++;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      result.errors.push(`Game ${gameDoc.id}: ${msg}`);
    }
  }

  return result;
}
